import { EGiftRegistryFailureReason } from '@mahawi/eshop-common/dist/src/types';
import { createSlice } from '@reduxjs/toolkit';

import { requestFailedMatcher } from '../request-failed/reducer';
import {
  type IGiftRegistryCreate,
  type IGiftRegistryCreateResponse,
  type IGiftRegistryExtendDeadline,
  type IGiftRegistryItemAdd,
  type IGiftRegistryItemConfirm,
  type IGiftRegistryItemConfirmResponse,
  type IGiftRegistryItemQuote,
  type IGiftRegistryItemQuoteResponse,
  type IGiftRegistryItemReject,
  type IGiftRegistryItemRemove,
  type IGiftRegistryItemsList,
  type IGiftRegistryItemsListResponse,
  type IGiftRegistryListResponse,
  type IGiftRegistryState,
  type IGiftRegistryUpdateVisibility,
} from './types';

const initialState: IGiftRegistryState = {
  isLoading: false,
  giftRegistries: [],
  giftRegistriesLoaded: false,
  createError: false,
  isLoadingItems: false,
  itemsByRegistry: {},
  itemsLoaded: false,
  itemAddError: false,
  isConfirming: false,
  confirmError: false,
  confirmedOrderUUID: null,
  confirmedRedirectLink: null,
};

const giftRegistrySlice = createSlice({
  name: 'GiftRegistry',
  initialState,
  reducers: {
    giftRegistryList: (state): void => {
      state.isLoading = true;
    },
    giftRegistryListResponse: (
      state,
      action: IGiftRegistryListResponse,
    ): void => {
      state.isLoading = false;
      state.giftRegistriesLoaded = true;

      if (action.payload.ok && action.payload.giftRegistries) {
        state.giftRegistries = action.payload.giftRegistries;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    giftRegistryCreate: (state, _action: IGiftRegistryCreate): void => {
      state.isLoading = true;
      state.createError = false;
    },
    giftRegistryCreateResponse: (
      state,
      action: IGiftRegistryCreateResponse,
    ): void => {
      state.isLoading = false;

      if (action.payload.ok && action.payload.giftRegistry) {
        state.giftRegistries = [
          action.payload.giftRegistry,
          ...state.giftRegistries,
        ];
      } else {
        state.createError = true;
      }
    },

    giftRegistryUpdateVisibility: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: IGiftRegistryUpdateVisibility,
    ): void => {
      state.isLoading = true;
    },
    giftRegistryUpdateVisibilityResponse: (
      state,
      action: IGiftRegistryCreateResponse,
    ): void => {
      state.isLoading = false;

      if (action.payload.ok && action.payload.giftRegistry) {
        const updated = action.payload.giftRegistry;
        state.giftRegistries = state.giftRegistries.map((giftRegistry) =>
          giftRegistry.uuid === updated.uuid ? updated : giftRegistry,
        );
      }
    },

    giftRegistryExtendDeadline: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: IGiftRegistryExtendDeadline,
    ): void => {
      state.isLoading = true;
    },
    giftRegistryExtendDeadlineResponse: (
      state,
      action: IGiftRegistryCreateResponse,
    ): void => {
      state.isLoading = false;

      if (action.payload.ok && action.payload.giftRegistry) {
        const updated = action.payload.giftRegistry;
        state.giftRegistries = state.giftRegistries.map((giftRegistry) =>
          giftRegistry.uuid === updated.uuid ? updated : giftRegistry,
        );
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    giftRegistryItemsList: (state, _action: IGiftRegistryItemsList): void => {
      state.isLoadingItems = true;
    },
    giftRegistryItemsListResponse: (
      state,
      action: IGiftRegistryItemsListResponse,
    ): void => {
      state.isLoadingItems = false;
      state.itemsLoaded = true;

      if (
        action.payload.ok &&
        action.payload.giftRegistryUUID &&
        action.payload.items
      ) {
        state.itemsByRegistry[action.payload.giftRegistryUUID] =
          action.payload.items;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    giftRegistryItemAdd: (state, _action: IGiftRegistryItemAdd): void => {
      state.isLoadingItems = true;
      state.itemAddError = false;
    },
    giftRegistryItemAddResponse: (
      state,
      action: IGiftRegistryItemsListResponse,
    ): void => {
      state.isLoadingItems = false;

      if (
        action.payload.ok &&
        action.payload.giftRegistryUUID &&
        action.payload.items
      ) {
        state.itemsByRegistry[action.payload.giftRegistryUUID] =
          action.payload.items;
      } else {
        state.itemAddError = true;
      }
    },

    giftRegistryItemRemove: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: IGiftRegistryItemRemove,
    ): void => {
      state.isLoadingItems = true;
    },
    giftRegistryItemRemoveResponse: (
      state,
      action: IGiftRegistryItemsListResponse,
    ): void => {
      state.isLoadingItems = false;

      if (
        action.payload.ok &&
        action.payload.giftRegistryUUID &&
        action.payload.items
      ) {
        state.itemsByRegistry[action.payload.giftRegistryUUID] =
          action.payload.items;
      }
    },

    giftRegistryItemConfirm: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: IGiftRegistryItemConfirm,
    ): void => {
      state.isConfirming = true;
      state.confirmError = false;
      state.confirmFailure = null;
      state.isPriceChanged = false;
      state.confirmedOrderUUID = null;
      state.confirmedRedirectLink = null;
    },
    giftRegistryItemConfirmResponse: (
      state,
      action: IGiftRegistryItemConfirmResponse,
    ): void => {
      state.isConfirming = false;

      if (action.payload.ok) {
        state.confirmedOrderUUID = action.payload.orderUUID ?? null;
        state.confirmedRedirectLink = action.payload.redirectLink ?? null;
      } else if (action.payload.failure) {
        state.confirmFailure = action.payload.failure;
      } else if (
        action.payload.reason === EGiftRegistryFailureReason.PRICE_CHANGED &&
        action.payload.quote
      ) {
        state.isPriceChanged = true;
        state.quote = action.payload.quote;
      } else {
        state.confirmError = true;
      }
    },

    giftRegistryItemQuote: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: IGiftRegistryItemQuote,
    ): void => {
      state.isQuoting = true;
      state.quoteError = false;
    },
    giftRegistryItemQuoteResponse: (
      state,
      action: IGiftRegistryItemQuoteResponse,
    ): void => {
      state.isQuoting = false;

      if (action.payload.ok && action.payload.quote) {
        state.quote = action.payload.quote;
      } else {
        state.quote = null;
        state.quoteError = true;
      }
    },

    giftRegistryItemReject: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: IGiftRegistryItemReject,
    ): void => {
      state.isLoadingItems = true;
    },
    giftRegistryItemRejectResponse: (
      state,
      action: IGiftRegistryItemsListResponse,
    ): void => {
      state.isLoadingItems = false;

      if (
        action.payload.ok &&
        action.payload.giftRegistryUUID &&
        action.payload.items
      ) {
        state.itemsByRegistry[action.payload.giftRegistryUUID] =
          action.payload.items;
      }
    },
  },
  extraReducers: (builder): void => {
    builder.addMatcher(requestFailedMatcher('GiftRegistry'), (state): void => {
      state.isLoading = false;
      state.isLoadingItems = false;

      if (state.isConfirming) {
        state.isConfirming = false;
        state.confirmError = true;
      }

      if (state.isQuoting) {
        state.isQuoting = false;
        state.quoteError = true;
      }
    });
  },
});

export const {
  giftRegistryList,
  giftRegistryListResponse,
  giftRegistryCreate,
  giftRegistryCreateResponse,
  giftRegistryUpdateVisibility,
  giftRegistryUpdateVisibilityResponse,
  giftRegistryExtendDeadline,
  giftRegistryExtendDeadlineResponse,
  giftRegistryItemsList,
  giftRegistryItemsListResponse,
  giftRegistryItemAdd,
  giftRegistryItemAddResponse,
  giftRegistryItemRemove,
  giftRegistryItemRemoveResponse,
  giftRegistryItemConfirm,
  giftRegistryItemConfirmResponse,
  giftRegistryItemQuote,
  giftRegistryItemQuoteResponse,
  giftRegistryItemReject,
  giftRegistryItemRejectResponse,
} = giftRegistrySlice.actions;

export default giftRegistrySlice.reducer;
